import { StyleSheet, Platform } from 'react-native'
import type { Theme } from '../../../theme/getTheme'

export function getAccountSettingsTabStyles(theme: Theme) {
  return StyleSheet.create({
    settingsScroll: {
      flex: 1,
      ...(Platform.OS === 'web' && {
        minHeight: 0 as never,
      }),
    },
    settingsScrollContent: {
      paddingBottom: theme.spacing.huge,
    },
    embeddedBody: {
      width: '100%',
      alignSelf: 'stretch',
    },
    section: {
      marginBottom: theme.spacing.xxl,
    },
    sectionFlushBottom: {
      marginBottom: 0,
    },
    sectionDivider: {
      height: StyleSheet.hairlineWidth,
      width: '100%',
      alignSelf: 'stretch',
      backgroundColor: theme.gray[200],
      marginBottom: theme.spacing.xl,
    },
    title: {
      fontSize: 16,
      fontWeight: '800',
      color: theme.gray[900],
      marginBottom: theme.spacing.md,
    },
    body: {
      fontSize: 14,
      color: theme.gray[700],
      marginBottom: theme.spacing.lg,
      lineHeight: 22,
    },
    input: {
      borderWidth: 1,
      borderColor: theme.gray[300],
      borderRadius: 8,
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.sm,
      fontSize: 16,
      color: theme.gray[900],
      backgroundColor: theme.background.cardElevated,
      marginBottom: theme.spacing.md,
      ...(Platform.OS === 'web' && {
        outlineStyle: 'none' as never,
      }),
    },
    primaryButton: {
      backgroundColor: theme.primary,
      paddingVertical: theme.spacing.sm,
      paddingHorizontal: theme.spacing.md,
      borderRadius: 10,
      alignSelf: 'flex-start',
      marginBottom: theme.spacing.md,
      flexDirection: 'row',
      alignItems: 'center',
    },
    primaryButtonPressed: {
      opacity: 0.88,
    },
    primaryButtonHover: {
      opacity: 0.94,
    },
    primaryButtonDisabled: {
      opacity: 0.5,
    },
    /** Primary actions laid out in a horizontal row (no extra bottom gap). */
    primaryButtonInRow: {
      marginBottom: 0,
      alignSelf: 'auto',
    },
    primaryButtonText: {
      color: theme.white,
      fontWeight: '700',
    },
    linkButton: {
      paddingVertical: theme.spacing.sm,
      marginBottom: theme.spacing.sm,
    },
    linkButtonPressed: {
      opacity: 0.85,
    },
    linkText: {
      color: theme.primary,
      fontWeight: '700',
      fontSize: 15,
    },
    /** Single-line checkbox row: no body marginBottom (would stretch row and mis-center the box). */
    ssoCheckboxRow: {
      flexDirection: 'row',
      alignItems: 'center',
      marginTop: theme.spacing.xs,
      gap: 10,
    },
    ssoCheckboxBox: {
      width: 20,
      height: 20,
      borderRadius: 4,
      borderWidth: 2,
      alignItems: 'center',
      justifyContent: 'center',
    },
    ssoCheckboxLabel: {
      flex: 1,
      flexShrink: 1,
      fontSize: 14,
      fontWeight: '500',
      color: theme.gray[700],
      marginBottom: 0,
      lineHeight: 20,
      ...Platform.select({
        android: { includeFontPadding: false },
        default: {},
      }),
    },
    dangerZone: {
      marginTop: 0,
      marginBottom: theme.spacing.xl,
      padding: theme.spacing.lg,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: theme.error + '55',
      backgroundColor: theme.error + '11',
    },
    dangerTitle: {
      fontSize: 15,
      fontWeight: '800',
      color: theme.error,
      marginBottom: theme.spacing.sm,
    },
    dangerButton: {
      backgroundColor: theme.error,
      paddingVertical: theme.spacing.sm,
      paddingHorizontal: theme.spacing.md,
      borderRadius: 10,
      alignSelf: 'flex-start',
      flexDirection: 'row',
      alignItems: 'center',
    },
    dangerButtonPressed: {
      opacity: 0.9,
    },
    dangerButtonText: {
      color: theme.white,
      fontWeight: '700',
    },
    leaveSection: {
      marginTop: 0,
      marginBottom: theme.spacing.lg,
      padding: theme.spacing.lg,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: theme.gray[200],
      backgroundColor: theme.background.card,
    },
    leaveTitle: {
      fontSize: 15,
      fontWeight: '800',
      color: theme.gray[900],
      marginBottom: theme.spacing.sm,
    },
    leaveSoleAdminHint: {
      fontSize: 14,
      color: theme.gray[600],
      marginBottom: theme.spacing.md,
      lineHeight: 20,
    },
    leaveOutlineButton: {
      borderWidth: 2,
      borderColor: theme.primary,
      paddingVertical: theme.spacing.sm,
      paddingHorizontal: theme.spacing.md,
      borderRadius: 10,
      alignSelf: 'flex-start',
      flexDirection: 'row',
      alignItems: 'center',
    },
    leaveOutlineButtonPressed: {
      opacity: 0.88,
    },
    leaveOutlineButtonDisabled: {
      opacity: 0.45,
    },
    leaveOutlineButtonHover: {
      opacity: 0.94,
    },
    leaveOutlineButtonText: {
      color: theme.primary,
      fontWeight: '700',
    },
    leaveOutlineButtonTextDisabled: {
      color: theme.gray[500],
    },
    deletePermanentSection: {
      marginTop: 0,
      padding: theme.spacing.lg,
      paddingBottom: theme.spacing.xl,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: theme.error + '66',
      backgroundColor: theme.error + '0d',
    },
    deletePermanentIntro: {
      fontSize: 14,
      color: theme.gray[600],
      marginBottom: theme.spacing.lg,
      lineHeight: 22,
    },
    deletePermanentLabel: {
      fontSize: 13,
      fontWeight: '700',
      color: theme.gray[900],
      marginBottom: theme.spacing.sm,
      marginTop: theme.spacing.xs,
    },
    deletePermanentExpectedName: {
      fontSize: 15,
      fontWeight: '800',
      color: theme.gray[900],
      marginBottom: theme.spacing.md,
    },
    deletePermanentInput: {
      borderWidth: 1,
      borderColor: theme.gray[300],
      borderRadius: 8,
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.sm,
      fontSize: 16,
      color: theme.gray[900],
      backgroundColor: theme.background.cardElevated,
      marginBottom: theme.spacing.lg,
      ...(Platform.OS === 'web' && {
        outlineStyle: 'none' as never,
      }),
    },
    deletePermanentSubmit: {
      marginTop: theme.spacing.sm,
    },
    deletePermanentDangerPressed: {
      opacity: 0.9,
    },
    deletePermanentDangerDisabled: {
      opacity: 0.45,
    },
    wcFieldLabel: {
      fontSize: 13,
      fontWeight: '700',
      color: theme.gray[900],
      marginBottom: theme.spacing.sm,
    },
    wcLinkedSquadName: {
      fontSize: 14,
      fontWeight: '700',
      color: theme.gray[900],
      marginBottom: theme.spacing.sm,
    },
    wcOutlineButton: {
      borderWidth: 1,
      borderColor: theme.gray[300],
      backgroundColor: 'transparent',
      paddingVertical: theme.spacing.sm,
      paddingHorizontal: theme.spacing.md,
      borderRadius: 10,
      alignSelf: 'flex-start',
    },
    wcOutlineButtonPressed: {
      opacity: 0.88,
    },
    wcOutlineButtonHover: {
      opacity: 0.94,
    },
    wcOutlineButtonText: {
      color: theme.primaryLight,
      fontWeight: '700',
      fontSize: 15,
    },
    membershipPolicyRowMuted: {
      opacity: 0.72,
    },
    membershipToggleRow: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: theme.spacing.sm,
    },
    membershipToggleHitArea: {
      width: 44,
      height: 44,
      alignItems: 'center',
      justifyContent: 'center',
      marginLeft: -theme.spacing.xs,
      marginRight: theme.spacing.xs,
    },
    membershipToggleHitAreaDisabled: {
      opacity: 0.55,
    },
    membershipToggleOuter: {
      width: 24,
      height: 24,
      borderRadius: 5,
      borderWidth: 2,
      borderColor: theme.gray[400],
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.background.card,
    },
    membershipToggleOuterOn: {
      borderColor: theme.primary,
      backgroundColor: theme.primary,
    },
    membershipToggleOuterPending: {
      borderColor: theme.gray[300],
      backgroundColor: theme.gray[100],
    },
    membershipToggleOuterLocked: {
      opacity: 0.65,
    },
    membershipToggleDot: {
      width: 10,
      height: 10,
      borderRadius: 2,
      backgroundColor: theme.white,
    },
    membershipToggleLabel: {
      fontSize: 15,
      fontWeight: '500',
      color: theme.gray[900],
      flexShrink: 1,
    },
    membershipToggleLabelMuted: {
      color: theme.gray[500],
    },
    membershipPolicyHint: {
      fontSize: 14,
      color: theme.gray[700],
      marginBottom: theme.spacing.md,
      lineHeight: 20,
    },
    /** Pending unlimited request — distinct color so status is obvious at a glance. */
    membershipPolicyHintPending: {
      color: theme.accentDark,
      fontWeight: '600',
    },
    membershipCancelRequestLink: {
      paddingVertical: theme.spacing.sm,
      marginBottom: theme.spacing.xs,
      alignSelf: 'flex-start',
    },
    membershipCancelRequestLinkPressed: {
      opacity: 0.85,
    },
    membershipCancelRequestLinkHover: {
      opacity: 0.92,
    },
    membershipCancelRequestText: {
      color: theme.gray[800],
      fontWeight: '600',
      fontSize: 14,
    },
    orgOnlyJoinTop: {
      marginTop: theme.spacing.lg,
    },
    orgInviteCopyLinkButton: {
      marginBottom: theme.spacing.md,
    },
  })
}
